// imports
import { useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FC, useCallback, useContext, useEffect, useState } from 'react';

// components
import CheckoutForm from 'components/main/CheckoutForm';
import { Alert } from '../Alert';

// interfaces, constants, helper
import { ENV, HTTP_STATUS } from 'constants/index';
import { CheckoutContext } from 'contexts/CheckoutContext';
import { useFindOnePriceAndProductsLazyQuery } from 'generated/graphql';
import { CheckoutProps, ParamType } from 'interfaces';
import { flexCenter } from 'theme/styleConstant';

export const Checkout: FC<CheckoutProps> = ({ staff, mode, onComplete }) => {
  const { id: staffId } = useParams<ParamType>();
  const { state } = useContext(CheckoutContext);
  const { priceId } = state;
  const [stripePromise] = useState(() => loadStripe(ENV.STRIPE_KEY));
  const [hasFetched, setHasFetched] = useState(false);

  const [findPriceAndProduct, { data, loading }] = useFindOnePriceAndProductsLazyQuery({
    onCompleted: (data) => {
      const { findOnePriceAndProducts } = data || {};
      const { response } = findOnePriceAndProducts || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setHasFetched(true);
      } else {
        Alert.error(response?.message ?? '');
      }
    },
    onError: ({ message }) => {
      Alert.error(message ?? '');
    },
  });

  const { findOnePriceAndProducts } = data || {};
  const { price } = findOnePriceAndProducts || {};
  const { amount, interval, isTrialEnded, billAbleAmount } = price || {};

  const fetchPriceAndProduct = useCallback(() => {
    findPriceAndProduct({
      variables: {
        getStripePriceInput: {
          id: priceId,
          ...(staffId && { staffId }),
          isStaff: mode === 'create-staff' ? true : false,
        },
      },
    });
  }, [findPriceAndProduct, priceId, staffId, mode]);

  useEffect(() => {
    if (!hasFetched && priceId) {
      fetchPriceAndProduct();
    }
  }, [fetchPriceAndProduct, hasFetched, priceId]);

  if (loading || !hasFetched) {
    return (
      <Box {...flexCenter} height="500px">
        <CircularProgress size={40} thickness={4} color="primary" />
      </Box>
    );
  }

  return (
    <Box mt={2}>
      <Elements stripe={stripePromise}>
        <CheckoutForm
          priceId={priceId}
          interval={interval}
          amount={amount}
          isTrialEnded={isTrialEnded}
          billAbleAmount={billAbleAmount}
          staff={staff}
          mode={mode}
          onComplete={onComplete}
        />
      </Elements>
    </Box>
  );
};
